import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCards } from "features/cards/useCards";
import { useCardBalances } from "features/cards/useCardBalances";
import type { CardRecord } from "components/card-row/CardRow";
import type { CardApiRecord } from "utils/AxiosUtil";

export interface UseIssuerCardsReturn {
  cards: CardRecord[];
  loadingCards: boolean;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useIssuerCards(): UseIssuerCardsReturn {
  const queryClient = useQueryClient();

  const {
    data: rawCards,
    isLoading: cardsLoading,
    error: cardsError,
  } = useCards();

  const addresses = useMemo(
    () => rawCards?.map((c: CardApiRecord) => c.aliasAddress) ?? [],
    [rawCards],
  );

  const { data: balances, isLoading: balancesLoading } =
    useCardBalances(addresses);

  const cards: CardRecord[] = useMemo(
    () =>
      rawCards?.map((c: CardApiRecord) => ({
        ...c,
        balance: balances?.[c.aliasAddress],
      })) ?? [],
    [rawCards, balances],
  );

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["cards"] });
    queryClient.invalidateQueries({ queryKey: ["balances", "cards"] });
  };

  return {
    cards,
    loadingCards: cardsLoading,
    isLoading: cardsLoading || balancesLoading,
    error: cardsError ? String(cardsError) : null,
    refresh,
  };
}
